import { create } from "zustand";
import { persist } from "zustand/middleware";
import api from "@/config/axiosConfig";
import { Currency } from "@/types";

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  INR: "₹",
  AUD: "A$",
  CAD: "C$",
  CHF: "CHF",
  CNY: "¥",
  SEK: "kr",
  NZD: "NZ$",
};

interface CurrencyState {
  selectedCurrency: Currency;
  conversionRates: Record<string, number>;
  isCurrencyLoading: boolean;
  setCurrency: (currency: Currency) => void;
  setIsCurrencyLoading: (isLoading: boolean) => void;
  convertAmount: (
    amount: number,
    from?: string,
    to?: string
  ) => Promise<number>;
  updateConversionRate: (from: string, to: string) => Promise<void>;
  getSymbol: (code: string) => string;
}

const defaultCurrency: Currency = {
  code: "USD",
  country: "United States",
  name: "US Dollar",
  symbol: "$",
};

const useCurrencyStore = create<CurrencyState>()(
  persist(
    (set, get) => ({
      selectedCurrency: defaultCurrency,
      conversionRates: {},
      isCurrencyLoading: false,

      setIsCurrencyLoading: (isLoading: boolean) => {
        set({ isCurrencyLoading: isLoading });
      },

      setCurrency: async (currency) => {
        // Start loading
        set({ isCurrencyLoading: true });

        try {
          // Update conversion rate when currency changes
          if (currency.code !== get().selectedCurrency.code) {
            await get().updateConversionRate(
              "USD",
              currency.code.toLowerCase()
            );
          }

          set({
            selectedCurrency: {
              ...currency,
              symbol: CURRENCY_SYMBOLS[currency.code] || currency.code,
            },
          });
        } catch (error) {
          console.error("Error setting currency:", error);
        }
      },

      updateConversionRate: async (from: string, to: string) => {
        try {
          const response = await api.content.get("/currency", {
            params: {
              from: from.toLowerCase(),
              to: to.toLowerCase(),
            },
          });

          set((state) => ({
            conversionRates: {
              ...state.conversionRates,
              [`${from}-${to}`]: response.data.rate,
            },
          }));
        } catch (error) {
          console.error("Failed to fetch conversion rate:", error);
        }
      },

      convertAmount: async (amount: number, from?: string, to?: string) => {
        const state = get();
        const fromCurrency = from?.toUpperCase() || "USD";
        const toCurrency = to?.toUpperCase() || state.selectedCurrency.code;

        // If converting to the same currency, return the original amount
        if (fromCurrency === toCurrency) return amount;

        const rateKey = `${fromCurrency}-${toCurrency}`;
        let rate = state.conversionRates[rateKey];

        // If we don't have the rate, fetch it
        if (!rate) {
          await state.updateConversionRate(fromCurrency, toCurrency);
          rate = get().conversionRates[rateKey];
        }

        return amount * (rate || 1);
      },

      getSymbol: (code: string) => {
        return CURRENCY_SYMBOLS[code] || code;
      },
    }),
    {
      name: "currency-storage",
      partialize: (state) => ({
        selectedCurrency: state.selectedCurrency,
        conversionRates: state.conversionRates,
      }),
    }
  )
);

export default useCurrencyStore;
